/**
 * ============================================================================
 * AXIOS HTTP CLIENT CONFIGURATION
 * ============================================================================
 *
 * This module configures the Axios HTTP client used throughout the application
 * for making API calls to the backend server. It includes:
 * - Request interceptors for automatic authentication
 * - Response interceptors for error handling and redirects
 * - Token management and header injection
 * - Error handling and user feedback
 *
 * PROCESS FLOW:
 * 1. Request Interceptor:
 *    - Checks if user is authenticated (has token in localStorage)
 *    - Injects Authorization header with JWT token
 *    - Handles edge cases (frozen headers, read-only properties)
 *
 * 2. Response Interceptor:
 *    - Intercepts all HTTP responses
 *    - Handles authentication errors (401) → redirects to login
 *    - Handles authorization errors (403) → shows access denied
 *    - Handles server errors (500) → shows generic error
 *    - Cleans up user session on logout
 *
 * BUSINESS LOGIC:
 * - Authentication: All API calls automatically include JWT token
 * - Token Management: Token is read from localStorage on each request
 * - Error Recovery: Failed auth attempts clear session and redirect
 * - Security: Token is never logged or exposed in error messages
 *
 * DATA FLOW:
 * Request → Interceptor (adds token) → Backend API → Response → Interceptor (handles errors)
 *
 * @fileoverview Axios HTTP client configuration with authentication and error handling
 * @author FinMatrix Development Team
 * @version 1.0.0
 */

import axios, { InternalAxiosRequestConfig, AxiosHeaders } from "axios";

/**
 * Create Axios instance with base configuration
 *
 * CONFIGURATION:
 * - baseURL: From environment variable or empty for Next.js API routes
 * - timeout: 30 seconds - prevents hanging requests
 * - headers: Default Content-Type for JSON API communication
 */
const API = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "", // Use env var or empty for Next.js routes
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

/**
 * ============================================================================
 * REQUEST INTERCEPTOR
 * ============================================================================
 *
 * PURPOSE:
 * Automatically injects JWT authentication token into every API request.
 * This ensures users don't have to manually add the token to each request.
 *
 * PROCESS FLOW:
 * 1. Check if code is running in browser (window exists)
 * 2. Retrieve JWT token from localStorage (set during login)
 * 3. If token exists, add it to request headers as "Authorization: Bearer <token>"
 * 4. Return the modified config to proceed with request
 *
 * BUSINESS LOGIC:
 * - Only runs in browser (SSR-safe check)
 * - Token is optional - if not present, request proceeds without auth
 * - Headers are modified in-place to avoid read-only property errors
 * - Fallback mechanism for frozen header objects
 *
 * ERROR HANDLING:
 * - If header modification fails (frozen object), attempts fallback
 * - Logs warning but doesn't crash the application
 * - Request proceeds without auth token if modification fails
 *
 * SECURITY:
 * - Token is stored in localStorage (client-side only)
 * - Token is never logged or exposed in error messages
 * - Token format: "Bearer <jwt-token>"
 */
API.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // STEP 1: Check if running in browser environment
    if (typeof window !== "undefined") {
      // STEP 2: Retrieve authentication token from localStorage
      const token = localStorage.getItem("authToken");

      // STEP 3: If token exists, inject it into request headers
      if (token) {
        // CRITICAL: Always set headers using the proper method
        // Don't rely on type casting - use AxiosHeaders methods
        if (!config.headers) {
          config.headers = {} as AxiosHeaders;
        }

        // Type-safe header handling
        // AxiosHeaders can have a set method or be a plain object
        type HeadersWithSet = AxiosHeaders & { set?: (key: string, value: string) => void; [key: string]: unknown };
        const headers = config.headers as HeadersWithSet;
        
        // Use set() method if available, otherwise direct assignment
        if (typeof headers.set === "function") {
          headers.set("Authorization", `Bearer ${token}`);
        } else {
          // Direct assignment with proper type handling
          headers["Authorization"] = `Bearer ${token}`;
          // Also set lowercase version for compatibility
          headers["authorization"] = `Bearer ${token}`;
        }

        // ALWAYS set on defaults as fallback
        API.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        console.log("✅ Token added to request headers");
      } else {
        console.warn("⚠️ No token found in localStorage");
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * ============================================================================
 * RESPONSE INTERCEPTOR
 * ============================================================================
 *
 * PURPOSE:
 * Intercepts all HTTP responses to handle errors and manage user session.
 * Provides centralized error handling and automatic redirects for auth failures.
 *
 * PROCESS FLOW:
 * 1. Successful responses pass through unchanged
 * 2. Error responses are intercepted and categorized
 * 3. Based on error type, appropriate action is taken:
 *    - 401 (Unauthorized) → Clear session, redirect to login
 *    - 403 (Forbidden) → Show access denied (TODO: implement user-friendly message)
 *    - 404 (Not Found) → Show not found message (TODO: implement)
 *    - 500 (Server Error) → Show generic error (TODO: implement)
 *    - Network errors → Show network error message (TODO: implement)
 *
 * BUSINESS LOGIC:
 * - Authentication Failures (401):
 *   - Token expired or invalid
 *   - User session is cleared (localStorage cleaned)
 *   - User redirected to login page
 *   - This prevents infinite loops of failed authenticated requests
 *
 * - Authorization Failures (403):
 *   - User is authenticated but lacks permissions
 *   - Currently only logs (TODO: show user-friendly toast notification)
 *
 * - Server Errors (500):
 *   - Backend server error or database issues
 *   - Currently only logs (TODO: show user-friendly error dialog)
 *
 * - Network Errors:
 *   - No response received (server down, network issue)
 *   - Currently only logs (TODO: show offline indicator)
 *
 * DATA FLOW:
 * Response → Interceptor → Error Handling → User Action (redirect/notification)
 */
API.interceptors.response.use(
  (response) => {
    // Successful responses (status 200-299)
    // Pass through unchanged - components handle success cases
    return response;
  },
  (error) => {
    // Error responses (status 400+ or network errors)
    // Only handle errors in browser environment
    if (typeof window !== "undefined") {
      // Check if error has a response (server responded with error status)
      if (error.response) {
        const { status } = error.response;

        // Handle different HTTP status codes
        switch (status) {
          case 401:
            const url = error.config?.url || "";
            /**
             * UNAUTHORIZED (401)
             *
             * BUSINESS LOGIC:
             * - Token is expired, invalid, or missing
             * - User authentication has failed
             * - Session must be cleared and user redirected to login
             *
             * PROCESS:
             * 1. Clear all authentication data from localStorage
             * 2. Clear user data and role information
             * 3. Redirect user to login page
             * 4. Error is still rejected so components can handle it
             */
            if (
              url.includes("/login") ||
              url.includes("/refresh") ||
              url.includes("/me")
            ) {
              localStorage.removeItem("authToken");
              localStorage.removeItem("user");
              localStorage.removeItem("role");
              window.location.href = "/login";
            }
            break;

          case 403:
            /**
             * FORBIDDEN (403)
             *
             * BUSINESS LOGIC:
             * - User is authenticated but doesn't have permission for this resource
             * - Should show user-friendly "Access Denied" message
             * - User stays on current page (no redirect)
             *
             * TODO: Implement toast notification showing "You don't have permission to access this resource"
             */
            // TODO: Show user-friendly error message via toast notification
            break;

          case 404:
            /**
             * NOT FOUND (404)
             *
             * BUSINESS LOGIC:
             * - Requested resource doesn't exist
             * - Could be deleted, moved, or never existed
             * - Should show appropriate error message
             *
             * TODO: Show "Resource not found" message or 404 page
             */
            // TODO: Show user-friendly error message
            break;

          case 500:
            /**
             * INTERNAL SERVER ERROR (500)
             *
             * BUSINESS LOGIC:
             * - Server-side error occurred (database, code, configuration)
             * - Not user's fault - should show generic error
             * - User should try again later or contact support
             *
             * TODO: Show generic error message: "Something went wrong. Please try again later."
             */
            // TODO: Show user-friendly error message
            break;

          default:
          /**
           * OTHER ERROR STATUSES (400, 422, 429, etc.)
           *
           * BUSINESS LOGIC:
           * - Other HTTP error codes (validation errors, rate limiting, etc.)
           * - Should show appropriate error message
           *
           * TODO: Show user-friendly error message based on error code
           */
          // TODO: Show user-friendly error message
        }
      } else if (error.request) {
        /**
         * NETWORK ERROR (No Response)
         *
         * BUSINESS LOGIC:
         * - Request was made but no response received
         * - Could be: server down, network issue, CORS problem, timeout
         * - User should be notified that network request failed
         *
         * TODO: Show network error message: "Unable to connect to server. Please check your connection."
         */
        // TODO: Show user-friendly error message
      } else {
        /**
         * REQUEST SETUP ERROR
         *
         * BUSINESS LOGIC:
         * - Error occurred while setting up the request (before sending)
         * - Could be: invalid URL, malformed request, configuration error
         * - Should log for debugging
         *
         * TODO: Show generic error message
         */
        // TODO: Show user-friendly error message
      }
    }

    // Always reject the promise so components can handle the error
    // Components can access error.response for status code and error message
    return Promise.reject(error);
  }
);

export default API;
